import { Box, Typography, useTheme } from '@mui/material';
import { iconBoxColors } from './iconCategory';
import { sheenSx } from './shimmer';
import { useUiPrefs } from '../../contexts/UiPrefsContext';

/**
 * v4 list row (DESIGN_SYSTEM.md section 8.2, #1377). Default row pattern inside
 * a SectionContainer: a tinted icon-box (or a caller-supplied `leadNode`, e.g. a
 * reconcile circle) on the left, a name + optional meta line, then hover-reveal
 * `actions`, an optional `trailing` node, and a right-aligned value block with
 * an optional `valueSub` line (e.g. a running balance). `actionsAfterValue`
 * (#2179) turns the tail round: value, then `trailing`, then `actions`, so the
 * amount reads first and the controls sit at the edge (the Budgets page and the
 * dashboard budget groups). Hidden actions keep their width either way, so the
 * value never moves on hover.
 *
 * Sizes (board): default = 32px box / 17px glyph / 14px name; compact (dense
 * desktop grids) = 28px box / 15px glyph / 13.5px name. All from theme.ds.size.
 *
 * Hover treatment (board): when the row is interactive or carries actions, the
 * hovered row paints the full-bleed `surface.rowHover` band (negative side
 * margins sized to escape the SectionContainer padding: 14px default density,
 * 16px compact rows in default-density cards). Actions are hidden until hover
 * on pointer-fine devices and always visible on touch. `forceActive` pins the
 * active state (row menus, tests).
 *
 * @param {object} props
 * @param {React.ReactNode} [props.icon]      Tabler icon element (color/size set here)
 * @param {string} [props.category]           icon-box tint + accent (checking|credit|...)
 * @param {React.ReactNode} [props.leadNode]  replaces the icon box (reconcile circle)
 * @param {React.ReactNode} props.name        primary label
 * @param {React.ReactNode} [props.meta]      secondary metadata line (12px dim)
 * @param {React.ReactNode} [props.value]     right-aligned value node (usually AmountDisplay)
 * @param {React.ReactNode} [props.valueSub]  11px dim line under the value
 * @param {React.ReactNode} [props.actions]   hover-revealed controls before the value
 * @param {React.ReactNode} [props.trailing]  always-visible node between actions and value
 * @param {boolean} [props.actionsAfterValue] value, then trailing, then actions (#2179)
 * @param {('default'|'compact')} [props.size] row density (board 32px vs 28px box)
 * @param {boolean} [props.divider]           bottom hairline (default true)
 * @param {boolean} [props.last]              drop the divider on the section's
 *                                             final row (grid layouts defeat the
 *                                             :last-child fallback)
 * @param {boolean} [props.archived]          neutral tint + muted name
 * @param {boolean} [props.forceActive]       pin the active/hover surface
 * @param {boolean} [props.hoverable]         apply the hover surface without onClick
 * @param {boolean} [props.shimmer]           sheen-sweep the icon box
 * @param {string} [props.shimmerDelay]       per-element sheen start offset
 * @param {function} [props.onClick]          click handler (implies hoverable)
 * @param {object} [props.sx]                 extra sx merged onto the row
 */
const Row = ({
  icon,
  category = 'neutral',
  leadNode,
  name,
  meta,
  value,
  valueSub,
  actions,
  trailing,
  actionsAfterValue = false,
  size = 'default',
  divider = true,
  last = false,
  archived = false,
  forceActive = false,
  hoverable = false,
  shimmer = false,
  shimmerDelay = '0ms',
  onClick,
  sx,
  ...rest
}) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const isCompact = size === 'compact';
  // #1979: the user's Icon shimmer preference gates every sheen.
  const { shimmer: shimmerPref } = useUiPrefs();
  const { tint, accent } = iconBoxColors(theme, archived ? 'neutral' : category);
  const interactive = hoverable || typeof onClick === 'function';
  const hasActions = actions != null;
  const actionsNode = hasActions ? <Box className="br-row-actions">{actions}</Box> : null;

  const boxSize = isCompact ? ds.size.iconBoxCompact : ds.size.iconBox;
  const glyph = isCompact ? ds.size.glyphCompact : ds.size.glyph;
  const nameFont = isCompact ? ds.font.rowCompact : ds.font.row;
  const vPad = isCompact ? 6 : 9;
  // Full-bleed hover band: negative margins escape the SectionContainer side
  // padding (default rows sit in compact-density cards: 14px; compact rows sit
  // in default-density desktop cards: 16px).
  const bleed = isCompact ? 16 : 14;

  const activeSx = {
    backgroundColor: palette.surface.rowHover,
    marginLeft: `-${bleed}px`,
    marginRight: `-${bleed}px`,
    paddingLeft: `${bleed}px`,
    paddingRight: `${bleed}px`,
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '11px',
        padding: `${vPad}px 0`,
        borderBottom: (divider && !last) ? `1px solid ${palette.divider}` : 'none',
        '&:last-child': { borderBottom: 'none' },
        transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}`,
        // Actions reveal on hover (pointer-fine) and stay visible on touch.
        '& .br-row-actions': {
          display: 'flex',
          alignItems: 'center',
          gap: `${ds.space[3]}px`,
          flexShrink: 0,
        },
        ...(hasActions && !forceActive && {
          '@media (hover: hover)': {
            '& .br-row-actions': { opacity: 0, pointerEvents: 'none' },
            '&:hover .br-row-actions': { opacity: 1, pointerEvents: 'auto' },
          },
        }),
        ...((interactive || hasActions) && {
          '@media (hover: hover)': { '&:hover': activeSx },
        }),
        ...(forceActive && activeSx),
        ...(interactive && { cursor: 'pointer' }),
        ...sx,
      }}
      {...rest}
    >
      {leadNode !== undefined ? leadNode : icon != null && (
        <Box
          sx={{
            width: boxSize,
            height: boxSize,
            borderRadius: `${ds.radius.icon}px`,
            backgroundColor: tint,
            color: accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { width: glyph, height: glyph },
            ...(shimmer && shimmerPref && sheenSx(theme, { delay: shimmerDelay })),
          }}
        >
          {icon}
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="div"
          noWrap
          sx={{
            fontSize: `${nameFont}px`,
            color: archived ? palette.text.secondary : palette.text.default,
          }}
        >
          {name}
        </Typography>
        {meta != null && (
          <Typography
            component="div"
            noWrap
            sx={{ fontSize: `${ds.font.dim}px`, color: palette.text.dim, marginTop: '1px' }}
          >
            {meta}
          </Typography>
        )}
      </Box>
      {!actionsAfterValue && actionsNode}
      {!actionsAfterValue && trailing}
      {(value != null || valueSub != null) && (
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          {value != null && (
            <Box
              sx={{
                fontSize: `${nameFont}px`,
                fontWeight: ds.font.weightMedium,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {value}
            </Box>
          )}
          {valueSub != null && (
            <Box
              sx={{
                fontSize: '11px',
                color: palette.text.dim,
                fontVariantNumeric: 'tabular-nums',
                marginTop: '1px',
              }}
            >
              {valueSub}
            </Box>
          )}
        </Box>
      )}
      {actionsAfterValue && trailing}
      {actionsAfterValue && actionsNode}
    </Box>
  );
};

export default Row;
